import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction, Prediction } from '../types/auction.types';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const ManageBids: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const { userProfile, isAuctioneer, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [auction, setAuction] = useState<Auction | null>(null);
  const [bids, setBids] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'executed'>('pending');
  const [executingBid, setExecutingBid] = useState<string | null>(null);
  const [selectedBid, setSelectedBid] = useState<Prediction | null>(null);
  const [finalPrice, setFinalPrice] = useState('');
  const [bidNotes, setBidNotes] = useState('');

  useEffect(() => {
    if (!isAuctioneer && !isAdmin) {
      toast.error('Access denied: Auctioneers only');
      navigate('/');
      return;
    }
    loadAuction();
    loadBids();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auctionId]);

  const loadAuction = async () => {
    if (!auctionId) return;

    try {
      const auctionDoc = await getDoc(doc(db, 'auctions', auctionId));
      
      if (auctionDoc.exists()) {
        const data = auctionDoc.data();
        const auctionData = {
          id: auctionDoc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Auction;

        if (!isAdmin && auctionData.createdBy !== userProfile?.uid) {
          toast.error('You do not have permission to manage bids for this auction');
          navigate('/auctioneer/dashboard');
          return;
        }

        setAuction(auctionData);
      }
    } catch (error) {
      console.error('Error loading auction:', error);
      toast.error('Failed to load auction');
    }
  };

  const loadBids = async () => {
    if (!auctionId) return;

    try {
      const predictionsRef = collection(db, 'predictions');
      const q = query(
        predictionsRef,
        where('auctionId', '==', auctionId),
        where('executeAsBid', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const bidsList: Prediction[] = [];
      
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        bidsList.push({
          id: docSnap.id,
          userId: data.userId,
          userEmail: data.userEmail,
          userName: data.userName,
          auctionId: data.auctionId,
          auctionTitle: data.auctionTitle,
          lotId: data.lotId,
          lotSku: data.lotSku,
          lotTitle: data.lotTitle,
          lotImage: data.lotImage,
          predictedPrice: data.predictedPrice,
          actualPrice: data.actualPrice,
          startingBid: data.startingBid,
          wagerAmount: data.wagerAmount,
          expertLowEstimate: data.expertLowEstimate,
          expertHighEstimate: data.expertHighEstimate,
          predictedOutsideRange: data.predictedOutsideRange,
          executeAsBid: data.executeAsBid,
          maxBidAmount: data.maxBidAmount,
          bidStatus: data.bidStatus,
          bidExecutedAt: data.bidExecutedAt?.toDate() || null,
          bidExecutedBy: data.bidExecutedBy,
          bidNotes: data.bidNotes,
          status: data.status,
          accuracy: data.accuracy,
          ringersEarned: data.ringersEarned,
          multiplier: data.multiplier,
          outcomeType: data.outcomeType,
          createdAt: data.createdAt?.toDate() || new Date(),
          settledAt: data.settledAt?.toDate() || null,
        } as Prediction);
      });

      // Sort by SKU
      bidsList.sort((a, b) => a.lotSku.localeCompare(b.lotSku));
      
      setBids(bidsList);
    } catch (error) {
      console.error('Error loading bids:', error);
      toast.error('Failed to load bids');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteBid = (bid: Prediction) => {
    setSelectedBid(bid);
    setFinalPrice('');
    setBidNotes('');
  };

  const submitExecution = async (status: 'executed' | 'won' | 'outbid' | 'declined') => {
    if (!selectedBid || !userProfile) return;

    if ((status === 'executed' || status === 'won') && !finalPrice) {
      toast.error('Please enter the final price');
      return;
    }

    setExecutingBid(selectedBid.id);

    try {
      const updateData: any = {
        bidStatus: status,
        bidExecutedAt: new Date(),
        bidExecutedBy: userProfile.uid,
        bidNotes: bidNotes || null,
      };

      if (status === 'won' && finalPrice) {
        updateData.actualPrice = parseFloat(finalPrice);
        updateData.status = 'won';
        updateData.settledAt = new Date();
      } else if (status === 'outbid' && finalPrice) {
        updateData.actualPrice = parseFloat(finalPrice);
        updateData.status = 'lost';
        updateData.settledAt = new Date();
      }

      await updateDoc(doc(db, 'predictions', selectedBid.id), updateData);
      
      toast.success(`Bid ${status} successfully`);
      setSelectedBid(null);
      setFinalPrice('');
      setBidNotes('');
      loadBids();
    } catch (error) {
      console.error('Error updating bid:', error);
      toast.error('Failed to update bid');
    } finally {
      setExecutingBid(null);
    }
  };

  const filteredBids = bids.filter(bid => {
    if (filter === 'all') return true;
    if (filter === 'pending') return bid.bidStatus === 'pending';
    if (filter === 'executed') return ['executed', 'won', 'outbid', 'declined'].includes(bid.bidStatus || '');
    return true;
  });

  // Group bids by lot
  const bidsByLot = filteredBids.reduce((acc, bid) => {
    if (!acc[bid.lotSku]) acc[bid.lotSku] = [];
    acc[bid.lotSku].push(bid);
    return acc;
  }, {} as Record<string, Prediction[]>);

  const getBidStatusBadge = (status?: string) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      executed: 'bg-blue-100 text-blue-800',
      won: 'bg-green-100 text-green-800',
      outbid: 'bg-red-100 text-red-800',
      declined: 'bg-gray-100 text-gray-800'
    };
    return badges[status as keyof typeof badges] || badges.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading bids...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Manage Left Bids</h1>
          <p className="text-gray-600 mt-1">{auction?.title}</p>
        </div>
        <button
          onClick={() => navigate(`/auctioneer/edit-auction/${auctionId}`)}
          className="btn-secondary"
        >
          Back to Auction
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Total Left Bids</p>
          <p className="text-3xl font-bold text-primary-600">{bids.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Pending</p>
          <p className="text-3xl font-bold text-yellow-600">
            {bids.filter(b => b.bidStatus === 'pending').length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Executed</p>
          <p className="text-3xl font-bold text-blue-600">
            {bids.filter(b => ['executed', 'won', 'outbid'].includes(b.bidStatus || '')).length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600 mb-1">Total Value</p>
          <p className="text-3xl font-bold text-green-600">
            ${bids.reduce((sum, b) => sum + (b.maxBidAmount || 0), 0).toFixed(0)}
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex space-x-2 border-b">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 font-semibold ${
            filter === 'all'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          All ({bids.length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 font-semibold ${
            filter === 'pending'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Pending ({bids.filter(b => b.bidStatus === 'pending').length})
        </button>
        <button
          onClick={() => setFilter('executed')}
          className={`px-4 py-2 font-semibold ${
            filter === 'executed'
              ? 'border-b-2 border-primary-600 text-primary-600'
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          Executed ({bids.filter(b => ['executed', 'won', 'outbid', 'declined'].includes(b.bidStatus || '')).length})
        </button>
      </div>

      {/* Bids by Lot */}
      {Object.keys(bidsByLot).length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600 text-lg">No left bids for this auction yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(bidsByLot).map(([sku, lotBids]) => {
            const highestBid = Math.max(...lotBids.map(b => b.maxBidAmount || 0));
            const bidCount = lotBids.length;
            
            return (
              <div key={sku} className="card">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{lotBids[0].lotTitle}</h3>
                    <p className="text-sm text-gray-600">SKU: {sku}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Highest Bid</p>
                    <p className="text-2xl font-bold text-green-600">${highestBid}</p>
                    <p className="text-xs text-gray-600">{bidCount} bid{bidCount !== 1 ? 's' : ''}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  {lotBids.map((bid) => (
                    <div key={bid.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-semibold">{bid.userName}</p>
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${getBidStatusBadge(bid.bidStatus)}`}>
                              {bid.bidStatus || 'pending'}
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Max Bid:</span>
                              <span className="ml-2 font-bold text-green-600">${bid.maxBidAmount}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Prediction:</span>
                              <span className="ml-2 font-semibold">${bid.predictedPrice}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Submitted:</span>
                              <span className="ml-2 text-gray-700">{format(new Date(bid.createdAt), 'MMM dd, h:mm a')}</span>
                            </div>
                          </div>
                          {bid.bidNotes && (
                            <p className="text-sm text-gray-600 mt-2">📝 {bid.bidNotes}</p>
                          )}
                        </div>
                        {bid.bidStatus === 'pending' && (
                          <button
                            onClick={() => handleExecuteBid(bid)}
                            className="btn-primary text-sm ml-4"
                          >
                            Execute
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Execute Bid Modal */}
      {selectedBid && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Execute Left Bid</h3>
            
            <div className="space-y-4 mb-6">
              <div className="p-4 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">Bidder</p>
                <p className="font-semibold">{selectedBid.userName}</p>
                <p className="text-xs text-gray-600">{selectedBid.userEmail}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">Lot</p>
                <p className="font-semibold">{selectedBid.lotTitle}</p>
                <p className="text-xs text-gray-600">SKU: {selectedBid.lotSku}</p>
              </div>

              <div className="p-4 bg-green-50 rounded">
                <p className="text-sm text-gray-600">Maximum Bid</p>
                <p className="text-2xl font-bold text-green-600">${selectedBid.maxBidAmount}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Final Sale Price
                </label>
                <input
                  type="number"
                  value={finalPrice}
                  onChange={(e) => setFinalPrice(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter final price"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={bidNotes}
                  onChange={(e) => setBidNotes(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="Add any notes about this bid..."
                />
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => submitExecution('won')}
                disabled={!finalPrice || executingBid === selectedBid.id}
                className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                ✓ Won - Winning Bidder
              </button>
              <button
                onClick={() => submitExecution('outbid')}
                disabled={!finalPrice || executingBid === selectedBid.id}
                className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                ✗ Outbid - Lost to Higher Bid
              </button>
              <button
                onClick={() => submitExecution('declined')}
                disabled={executingBid === selectedBid.id}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50"
              >
                ⊘ Declined - Did Not Execute
              </button>
              <button
                onClick={() => setSelectedBid(null)}
                className="w-full btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBids;